<ui-panel
  [panelTitle]="node().displayName || node().stepCode || node().id"
  [noGrow]="true"
>
  <p>
    {{
      node().typeCode === "terminal"
        ? "End step"
        : node().typeCode === "automated_step"
          ? "Automated activity"
          : "Staff task"
    }}
  </p>
  @if (node().typeCode === "terminal") {
    @if (terminalResult(); as result) {
      <dl>
        <dt>Workflow result</dt>
        <dd>{{ result.label }}</dd>
      </dl>
      <p class="inspection-note">{{ result.detail }}</p>
      @if (result.raw !== null) {
        <details>
          <summary>Technical details</summary>
          <p>
            <code>{{ result.raw }}</code>
          </p>
        </details>
      }
    }
  } @else if (overview(); as step) {
    <dl>
      <dt>Who does this work</dt>
      <dd>
        {{ step.responsibility.label }} · {{ step.responsibility.detail }}
      </dd>
      <dt>Completion requirements</dt>
      <dd>
        @for (requirement of step.requirements; track requirement.key) {
          <p>{{ requirement.label }} · {{ requirement.detail }}</p>
        } @empty {
          <p>No authored requirements.</p>
        }
        <p>{{ step.completionDetail }}</p>
      </dd>
      <dt>Next</dt>
      <dd>
        @for (path of step.paths; track path.key) {
          <p>{{ path.label }} → {{ path.destinationLabel }}</p>
        } @empty {
          <p>
            No configured path. Inspect Routing to configure the next
            destination.
          </p>
        }
      </dd>
    </dl>
  } @else if (node().typeCode === "automated_step") {
    <p>{{ presentation().activityNote }}</p>
    @if (presentation().activity; as activity) {
      <dl>
        <dt>Saved activity</dt>
        <dd>
          <p>{{ activity.reference }}</p>
          <ng-container
            [ngTemplateOutlet]="commandDetails"
            [ngTemplateOutletContext]="{ $implicit: activity }"
          />
        </dd>
        <dt>Results and destinations · {{ presentation().results.length }}</dt>
        <dd>
          <ul class="inspection-list">
            @for (result of presentation().results; track result.key) {
              <li>
                <strong>{{ result.label }}</strong> → {{ result.destination }}
                <p class="inspection-identity">
                  <code>{{ result.commandOutcome }}</code> →
                  <code>{{ result.routeOutcome }}</code>
                </p>
                <p>
                  {{ result.detail }}
                  @if (result.needsInspection) {
                    <span>Needs inspection.</span>
                  }
                </p>
              </li>
            } @empty {
              <li>No saved result mappings. Inspect Activity and Routing.</li>
            }
          </ul>
        </dd>
      </dl>
    }
  }
  @if (node().typeCode === "human_step") {
    @if (presentation().hasDeclaration) {
      <dl>
        <dt>Forms · {{ presentation().forms.length }}</dt>
        <dd>
          <ul class="inspection-list">
            @for (form of presentation().forms; track form.key) {
              <li>
                <strong>{{ form.label }}</strong>
                <p>{{ form.detail }}</p>
                <ng-container
                  [ngTemplateOutlet]="commandDetails"
                  [ngTemplateOutletContext]="{ $implicit: form }"
                />
              </li>
            } @empty {
              <li>No authored form settings or main form interaction.</li>
            }
          </ul>
        </dd>
        <dt>Step actions · {{ presentation().actions.length }}</dt>
        <dd>
          <ul class="inspection-list">
            @for (action of presentation().actions; track action.key) {
              <li>
                <strong>{{ action.label }}</strong>
                <ng-container
                  [ngTemplateOutlet]="commandDetails"
                  [ngTemplateOutletContext]="{ $implicit: action }"
                />
              </li>
            } @empty {
              <li>No separate step actions configured.</li>
            }
          </ul>
        </dd>
      </dl>
      @if (presentation().moduleCommands.length) {
        <details>
          <summary>Technical details</summary>
          <p class="inspection-note">
            {{ presentation().moduleCommands.length }} saved commands are
            managed by Staff page modules.
          </p>
          <ul class="inspection-list">
            @for (command of presentation().moduleCommands; track command.key) {
              <li>
                <strong>{{ command.reference }}</strong>
                <ng-container
                  [ngTemplateOutlet]="commandDetails"
                  [ngTemplateOutletContext]="{ $implicit: command }"
                />
              </li>
            }
          </ul>
        </details>
      }
      @if (presentation().formNote) {
        <p class="inspection-note">{{ presentation().formNote }}</p>
      }
    } @else {
      <p>
        The form and action inventory is not declared in this document format.
        Inspect Work for its retained configuration.
      </p>
    }
  }
  <p class="inspection-note">
    Working document settings. Saved validation and runtime progress are
    separate.
  </p>
  <button
    uiButton
    type="button"
    severity="primary"
    [outlined]="false"
    [disabled]="blocked()"
    (click)="openEditor.emit()"
  >
    Open step editor
  </button>
</ui-panel>

<ng-template #commandDetails let-command>
  <details>
    <summary>Saved command details</summary>
    <p>{{ command.reference }}</p>
    <dl>
      @for (pin of command.pins; track pin.label) {
        <dt>{{ pin.label }}</dt>
        <dd>
          <code>{{ pin.value }}</code>
        </dd>
      }
    </dl>
    <p class="inspection-note">
      Current installation and compatibility are checked in the editor.
    </p>
  </details>
</ng-template>
